import { Metrics } from 'MeetingsPublic/utils/metrics';
import * as ActionTypes from 'MeetingsLib/redux/actions/ActionTypes';
const STANDARD_FIELDS = new Set(['email', 'firstName', 'lastName']);
const PHONE_FIELDS = new Set(['phone', 'mobilephone']);
export function normalizeFieldType(fieldName) {
  if (STANDARD_FIELDS.has(fieldName)) return 'standard';
  if (PHONE_FIELDS.has(fieldName)) return 'phone';
  return 'custom';
}
export function normalizeErrorType(error) {
  var _error$responseJSON;
  if (!error) return 'unknown';
  if (typeof error === 'string') {
    if (error.includes('TEMPORARILY_UNAVAILABLE')) return 'temporarily_unavailable';
    return error ? 'other' : 'unknown';
  }
  if (((_error$responseJSON = error.responseJSON) === null || _error$responseJSON === void 0 ? void 0 : _error$responseJSON.errorType) === 'TIME_SLOT_NOT_AVAILABLE') return 'time_conflict';
  if (!error.status || error.status === 0) return 'network_error';
  if (error.status === 400) return 'bad_request';
  if (error.status === 404) return 'not_found';
  if (error.status >= 500) return 'server_error';
  return 'unknown';
}
const CANCEL_ACTIONS = new Set([ActionTypes.MEETING_CANCEL_STARTED, ActionTypes.MEETING_CANCEL_SUCCEEDED, ActionTypes.MEETING_CANCEL_FAILED]);
const RESCHEDULE_ACTIONS = new Set([ActionTypes.MEETING_RESCHEDULE_STARTED, ActionTypes.MEETING_RESCHEDULE_SUCCEEDED, ActionTypes.MEETING_RESCHEDULE_FAILED]);
function getFlow(actionType, state) {
  if (CANCEL_ACTIONS.has(actionType)) return 'cancel';
  if (RESCHEDULE_ACTIONS.has(actionType)) return 'reschedule';
  return state.isRescheduling ? 'reschedule' : 'new_booking';
}
export default (({
  getState
}) => {
  let availabilityFetchStartAt = 0;
  let availabilityLoadedAt = 0;
  let bookingStartAt = 0;
  let cancelStartAt = 0;
  let rescheduleStartAt = 0;
  let paymentStartAt = 0;
  let emailValidationStartAt = 0;
  return next => action => {
    var _bookInfoAny$customPa;
    next(action);
    const state = getState();
    const embedded = String(!!state.embedded);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bookInfoAny = state.bookInfo;
    const paid = String(!!(bookInfoAny !== null && bookInfoAny !== void 0 && (_bookInfoAny$customPa = bookInfoAny.customParams) !== null && _bookInfoAny$customPa !== void 0 && _bookInfoAny$customPa.paymentLinkInfo));
    const flow = getFlow(action.type, state);
    const dims = {
      embedded,
      flow,
      paid
    };
    switch (action.type) {
      // Availability
      case ActionTypes.PUBLIC_AVAILABILITY_INFO_FETCH_STARTED:
        {
          availabilityFetchStartAt = Date.now();
          availabilityLoadedAt = 0;
          Metrics.counter('availability-fetch-started', dims).increment();
          break;
        }
      case ActionTypes.PUBLIC_AVAILABILITY_INFO_FETCH_SUCCEEDED:
        {
          availabilityLoadedAt = Date.now();
          Metrics.counter('availability-fetch-succeeded', dims).increment();
          if (availabilityFetchStartAt > 0) {
            Metrics.timer('availability-fetch-duration').update(availabilityLoadedAt - availabilityFetchStartAt);
            availabilityFetchStartAt = 0;
          }
          break;
        }
      case ActionTypes.PUBLIC_AVAILABILITY_INFO_FETCH_FAILED:
        {
          var _action$payload;
          Metrics.counter('availability-fetch-failed', Object.assign({}, dims, {
            error_type: normalizeErrorType((_action$payload = action.payload) === null || _action$payload === void 0 ? void 0 : _action$payload.error)
          })).increment();
          availabilityFetchStartAt = 0;
          break;
        }
      case ActionTypes.LOG_ERROR_PAGE_SHOWN:
        {
          Metrics.counter('no-availability-error-shown', dims).increment();
          break;
        }

      // Date/time selection
      case ActionTypes.SET_EVENT_DATE:
        {
          if (action.date) {
            Metrics.counter('date-selected', dims).increment();
          }
          break;
        }
      case ActionTypes.SET_EVENT_DATE_TIME:
        {
          if (action.dateTime) {
            Metrics.counter('time-selected', dims).increment();
            if (availabilityLoadedAt > 0) {
              Metrics.timer('time-to-time-selection').update(Date.now() - availabilityLoadedAt);
            }
          }
          break;
        }
      case ActionTypes.SET_EVENT_DURATION:
        {
          Metrics.counter('duration-changed', dims).increment();
          break;
        }
      case ActionTypes.SET_TIMEZONE:
        {
          Metrics.counter('timezone-changed', dims).increment();
          break;
        }

      // Form
      case ActionTypes.SET_FORM_FIELD:
        {
          var _action$payload$field, _action$payload2;
          Metrics.counter('form-field-interaction', Object.assign({}, dims, {
            field_type: normalizeFieldType((_action$payload$field = (_action$payload2 = action.payload) === null || _action$payload2 === void 0 ? void 0 : _action$payload2.fieldName) !== null && _action$payload$field !== void 0 ? _action$payload$field : '')
          })).increment();
          break;
        }
      case ActionTypes.SET_FORM_STATE:
        {
          Metrics.counter('form-completed', dims).increment();
          break;
        }
      case ActionTypes.ADD_GUEST_EMAIL:
        {
          Metrics.counter('guest-email-added', dims).increment();
          break;
        }
      case ActionTypes.REMOVE_GUEST_EMAIL:
        {
          Metrics.counter('guest-email-removed', dims).increment();
          break;
        }

      // Async email validation — initial and staged variants share the same counters/timer
      case ActionTypes.ASYNC_INITIAL_EMAIL_VALIDATION_STARTED:
      case ActionTypes.ASYNC_STAGED_EMAIL_VALIDATION_STARTED:
        {
          emailValidationStartAt = Date.now();
          Metrics.counter('async-email-validation-started', dims).increment();
          break;
        }
      case ActionTypes.ASYNC_INITIAL_EMAIL_VALIDATION_COMPLETED:
      case ActionTypes.ASYNC_STAGED_EMAIL_VALIDATION_COMPLETED:
        {
          Metrics.counter('async-email-validation-completed', dims).increment();
          if (emailValidationStartAt > 0) {
            Metrics.timer('async-email-validation-duration').update(Date.now() - emailValidationStartAt);
            emailValidationStartAt = 0;
          }
          break;
        }
      case ActionTypes.ASYNC_INITIAL_EMAIL_VALIDATION_FAILED:
      case ActionTypes.ASYNC_STAGED_EMAIL_VALIDATION_FAILED:
        {
          Metrics.counter('async-email-validation-failed', dims).increment();
          emailValidationStartAt = 0;
          break;
        }

      // Legal consent
      case ActionTypes.LEGAL_CONSENT_FORM_SHOWN:
        {
          Metrics.counter('consent-form-shown', dims).increment();
          break;
        }
      case ActionTypes.SET_CONSENT_RESPONSES:
        {
          Metrics.counter('consent-completed', dims).increment();
          break;
        }

      // Payment
      case ActionTypes.SET_PAYMENT_SESSION_ID:
        {
          Metrics.counter('payment-session-created', dims).increment();
          break;
        }
      case ActionTypes.PAID_MEETING_BOOKING_PAYMENT_PROCESSING:
        {
          paymentStartAt = Date.now();
          Metrics.counter('payment-processing-started', dims).increment();
          break;
        }
      case ActionTypes.PAID_MEETING_BOOKING_PAYMENT_SUCCESSFUL:
        {
          Metrics.counter('payment-succeeded', dims).increment();
          if (paymentStartAt > 0) {
            Metrics.timer('payment-duration').update(Date.now() - paymentStartAt);
            paymentStartAt = 0;
          }
          break;
        }
      case ActionTypes.PAID_MEETING_BOOKING_PAYMENT_FAILED:
        {
          Metrics.counter('payment-failed', dims).increment();
          paymentStartAt = 0;
          break;
        }

      // Booking submission
      case ActionTypes.MEETING_CREATE_STARTED:
        {
          bookingStartAt = Date.now();
          Metrics.counter('booking-started', dims).increment();
          break;
        }
      case ActionTypes.MEETING_CREATE_SUCCEEDED:
        {
          const now = Date.now();
          Metrics.counter('booking-succeeded', dims).increment();
          if (bookingStartAt > 0) {
            Metrics.timer('booking-submission-duration').update(now - bookingStartAt);
            bookingStartAt = 0;
          }
          if (availabilityLoadedAt > 0) {
            Metrics.timer('end-to-end-duration').update(now - availabilityLoadedAt);
          }
          break;
        }
      case ActionTypes.MEETING_CREATE_FAILED:
        {
          var _action$payload3;
          Metrics.counter('booking-failed', Object.assign({}, dims, {
            error_type: normalizeErrorType((_action$payload3 = action.payload) === null || _action$payload3 === void 0 ? void 0 : _action$payload3.error)
          })).increment();
          bookingStartAt = 0;
          break;
        }
      case ActionTypes.BOOKING_TIME_CONFLICT:
        {
          Metrics.counter('booking-time-conflict', dims).increment();
          break;
        }

      // Cancel
      case ActionTypes.MEETING_CANCEL_STARTED:
        {
          cancelStartAt = Date.now();
          Metrics.counter('cancel-started', dims).increment();
          break;
        }
      case ActionTypes.MEETING_CANCEL_SUCCEEDED:
        {
          Metrics.counter('cancel-succeeded', dims).increment();
          if (cancelStartAt > 0) {
            Metrics.timer('cancel-duration').update(Date.now() - cancelStartAt);
            cancelStartAt = 0;
          }
          break;
        }
      case ActionTypes.MEETING_CANCEL_FAILED:
        {
          var _action$payload4;
          Metrics.counter('cancel-failed', Object.assign({}, dims, {
            error_type: normalizeErrorType((_action$payload4 = action.payload) === null || _action$payload4 === void 0 ? void 0 : _action$payload4.error)
          })).increment();
          cancelStartAt = 0;
          break;
        }

      // Reschedule
      case ActionTypes.MEETING_RESCHEDULE_STARTED:
        {
          rescheduleStartAt = Date.now();
          Metrics.counter('reschedule-started', dims).increment();
          break;
        }
      case ActionTypes.MEETING_RESCHEDULE_SUCCEEDED:
        {
          Metrics.counter('reschedule-succeeded', dims).increment();
          if (rescheduleStartAt > 0) {
            Metrics.timer('reschedule-duration').update(Date.now() - rescheduleStartAt);
            rescheduleStartAt = 0;
          }
          break;
        }
      case ActionTypes.MEETING_RESCHEDULE_FAILED:
        {
          var _action$payload5;
          Metrics.counter('reschedule-failed', Object.assign({}, dims, {
            error_type: normalizeErrorType((_action$payload5 = action.payload) === null || _action$payload5 === void 0 ? void 0 : _action$payload5.error)
          })).increment();
          rescheduleStartAt = 0;
          break;
        }
      default:
    }
  };
});